(() => {
  if (window.PMXHome6) return;

  const SIZE_STORAGE_KEY = 'pmx-h6-size';
  const SIZE_VALUES = ['xs', 's', 'm', 'l', 'xl'];
  const INK = '#242424';
  const PAPER = '#f7f5ef';
  const DOG_ACCENT = '#f28a2e';
  const CAT_ACCENT = '#4f7a8c';
  const GROOM_ACCENT = '#8a7355';
  const WHITE = '#ffffff';
  const AUTO_MODE_DELAY = 7600;

  const MODE_ACCENT = { dog: DOG_ACCENT, cat: CAT_ACCENT, grooming: GROOM_ACCENT };
  const DOG_NEEDS = ['FOOD', 'TREATS', 'LEASH', 'TOYS', 'BED', 'COAT'];
  const CAT_NEEDS = ['FOOD', 'LITTER', 'SCRATCH', 'TOYS', 'PERCH', 'CARE'];
  const GROOM_PATH = ['BOOK', 'ARRIVE', 'BATHE', 'TRIM', 'FINISH'];
  const CAT_LIFESTYLE = ['ENERGY', 'CALM', 'INDOOR', 'SENIOR', 'PLAY'];
  const QUICK_SERVICE_MARKERS = ['FACE', 'EARS', 'TEETH', 'NAILS', 'TIDY'];

  const instances = new Map();
  const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const precisionQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const lerp = (from, to, amount) => from + (to - from) * amount;

  function readStoredSize() {
    try {
      const value = window.sessionStorage.getItem(SIZE_STORAGE_KEY);
      return SIZE_VALUES.includes(value) ? value : null;
    } catch {
      return null;
    }
  }

  function writeStoredSize(value) {
    try {
      window.sessionStorage.setItem(SIZE_STORAGE_KEY, value);
    } catch {
      /* sessionStorage may be unavailable (private mode); size state stays in-memory only */
    }
  }

  function modeAccent(mode) {
    return MODE_ACCENT[mode] || INK;
  }

  // ---------------------------------------------------------------------
  // Single global scheduler. Every dock spring and every canvas scene
  // registers/unregisters a per-frame tick here; nothing else in this
  // module ever creates a separate animation-frame loop or repeating timer.
  // ---------------------------------------------------------------------
  const scheduler = (() => {
    const tickers = new Set();
    let frame = 0;

    const loop = (timestamp) => {
      frame = tickers.size ? window.requestAnimationFrame(loop) : 0;
      for (const tick of tickers) tick(timestamp);
    };

    return {
      add(tick) {
        tickers.add(tick);
        if (!frame) frame = window.requestAnimationFrame(loop);
      },
      remove(tick) {
        tickers.delete(tick);
        if (!tickers.size && frame) {
          window.cancelAnimationFrame(frame);
          frame = 0;
        }
      },
    };
  })();

  // ---------------------------------------------------------------------
  // Highlight tracking shared by product rails, service rows, quick items,
  // front-service controls and weight cells: hover and keyboard focus
  // drive the same state.
  // ---------------------------------------------------------------------
  function wireHighlight(root, selector, signal, onChange) {
    let active = null;
    const set = (element) => {
      if (active === element) return;
      active = element;
      onChange(active);
    };
    root.querySelectorAll(selector).forEach((element) => {
      element.addEventListener('pointerenter', () => set(element), { signal, passive: true });
      element.addEventListener('pointerleave', () => {
        if (active === element) set(null);
      }, { signal, passive: true });
      element.addEventListener('focus', () => set(element), { signal });
      element.addEventListener('blur', () => {
        if (active === element) set(null);
      }, { signal });
    });
  }

  function wireQuickview(root, signal) {
    let active = null;
    let effect = null;
    let frame = null;
    let nativeListeners = null;
    const visibleControls = (modal, selector) => Array.from(modal.querySelectorAll(selector)).filter((element) => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
    const releaseNativeListeners = () => {
      if (!nativeListeners) return;
      for (const name of ['focusin', 'focusout', 'keydown']) {
        if (nativeListeners[name]) document.removeEventListener(name, nativeListeners[name]);
      }
      nativeListeners = null;
    };
    const focusNativeModal = (event) => {
      const current = active;
      const alpine = window.Alpine;
      if (!current || typeof alpine?.nextTick !== 'function') return;
      if (event?.detail?.productUrl && event.detail.productUrl !== current.link.getAttribute('href')) return;
      alpine.nextTick(() => {
        if (active !== current || !current.quickview.show || signal.aborted) return;
        const focusStore = alpine.store('xFocusElement');
        if (focusStore) focusStore.windowWidth = window.innerWidth;
        releaseNativeListeners();
        const modal = document.getElementById('QuickView');
        const originalSelector = focusStore?.focusableElements;
        const controls = modal && originalSelector ? visibleControls(modal, originalSelector) : [];
        // The provider snapshots endpoints. Supply visible controls through its
        // existing selector contract, then restore that shared setting immediately.
        controls.forEach((element) => element.setAttribute('data-pmx-h6-modal-focusable', ''));
        if (focusStore && controls.length) focusStore.focusableElements = '[data-pmx-h6-modal-focusable]';
        try {
          current.quickview.focusQuickView?.('QuickView', 'QuickViewClose');
        } finally {
          if (focusStore) focusStore.focusableElements = originalSelector;
          controls.forEach((element) => element.removeAttribute('data-pmx-h6-modal-focusable'));
        }
        if (window.innerWidth >= 1025) {
          nativeListeners = focusStore?.listeners ? { ...focusStore.listeners } : null;
        } else {
          // Eurus intentionally skips its desktop trap on small screens.
          document.getElementById('QuickViewClose')?.focus({ preventScroll: true });
        }
      });
    };
    // Native content can exceed the viewport, preventing its full-intersection
    // focus trigger. Reuse the provider trap after x-show and dynamic controls settle.
    document.addEventListener('eurus:quickview:loaded', focusNativeModal, { signal });
    window.addEventListener('resize', focusNativeModal, { signal });
    document.addEventListener('keydown', (event) => {
      if (!active?.quickview.show || event.key !== 'Tab') return;
      const modal = document.getElementById('QuickView');
      if (!modal) return;
      const selector = window.Alpine.store('xFocusElement')?.focusableElements || 'button, [href], input, select, textarea, [tabindex]:not([tabindex^="-"])';
      const controls = visibleControls(modal, selector);
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!modal.contains(event.target) || event.shiftKey && event.target === first || !event.shiftKey && event.target === last) {
        event.preventDefault();
        event.stopImmediatePropagation();
        (event.shiftKey ? last : first).focus();
      }
    }, { signal, capture: true });
    const cleanup = (restoreFocus = false) => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      if (effect) window.Alpine.release(effect);
      effect = null;
      releaseNativeListeners();
      if (!active) return;
      const { link, originalId, fallback } = active;
      active = null;
      fallback.remove();
      if (link.id === 'button_quickview') link.id = originalId;
      if (restoreFocus && link.isConnected) link.focus({ preventScroll: true });
    };
    signal.addEventListener('abort', () => cleanup(), { once: true });
    root.querySelectorAll('[data-pmx-h6-quickview]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const alpine = window.Alpine;
        const quickview = alpine?.store('xQuickView');
        const content = document.getElementById('quickview-product-content');
        if (!quickview?.enabled || typeof quickview.load !== 'function' || typeof quickview.open !== 'function' || !alpine.effect || !alpine.release || !content?.parentElement) return;
        cleanup();
        const previous = document.getElementById('button_quickview');
        if (previous) previous.removeAttribute('id');
        const originalId = link.id;
        link.id = 'button_quickview';
        const fallback = document.createElement('a');
        fallback.className = 'pmx-h6-quickview-fallback';
        fallback.href = link.href;
        fallback.textContent = 'View full product details';
        // Outside native replaced content: remains usable during loading and failed requests.
        content.parentElement.insertBefore(fallback, content);
        active = { link, originalId, fallback, quickview };
        quickview.load(link.getAttribute('href'), link, link.dataset.pmxH6Quickview);
        quickview.open();
        focusNativeModal();
        effect = alpine.effect(() => {
          if (!quickview.show && active) {
            // Let the provider remove its focus trap using button_quickview first.
            frame = window.requestAnimationFrame(() => cleanup(true));
          }
        });
        event.preventDefault();
      }, { signal });
    });
  }

  function wireRail(rail, signal) {
    const prev = rail.parentElement?.querySelector('[data-pmx-h6-rail-prev]');
    const next = rail.parentElement?.querySelector('[data-pmx-h6-rail-next]');
    const updateDisabled = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      if (prev) prev.disabled = rail.scrollLeft <= 1;
      if (next) next.disabled = rail.scrollLeft >= max - 1;
    };
    const scrollBy = (direction) => {
      rail.scrollBy({
        left: direction * rail.clientWidth,
        behavior: reducedQuery.matches ? 'auto' : 'smooth',
      });
    };
    prev?.addEventListener('click', () => scrollBy(-1), { signal });
    next?.addEventListener('click', () => scrollBy(1), { signal });
    rail.addEventListener('scroll', updateDisabled, { signal, passive: true });
    updateDisabled();
  }

  // ---------------------------------------------------------------------
  // Pointer tracking: delegated on the root so it works across panel
  // swaps without rebinding, and never attaches to the (pointer-events:
  // none) decorative canvas itself. Normalized against the active media
  // element's own box so it stays meaningful after resize/reflow.
  // ---------------------------------------------------------------------
  function wirePointer(root, mediaSelector, signal, sceneState, onChange) {
    const updateFromEvent = (event) => {
      const media = event.target.closest?.(mediaSelector);
      if (!media) return;
      const bounds = media.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      sceneState.pointer.x = clamp((event.clientX - bounds.left) / bounds.width, 0, 1);
      sceneState.pointer.y = clamp((event.clientY - bounds.top) / bounds.height, 0, 1);
      sceneState.pointer.active = true;
      onChange?.();
    };
    root.addEventListener('pointermove', updateFromEvent, { signal, passive: true });
    root.addEventListener('pointerdown', updateFromEvent, { signal, passive: true });
    root.addEventListener('pointerleave', () => {
      if (!sceneState.pointer.active) return;
      sceneState.pointer.active = false;
      onChange?.();
    }, { signal, passive: true });
  }

  // Small deterministic offset derived from pointer position, centred on
  // zero; stays at zero for reduced motion or before any pointer activity.
  function pointerDrift(state, magnitude) {
    if (reducedQuery.matches || !state.pointer.active) return { dx: 0, dy: 0 };
    return {
      dx: (state.pointer.x - 0.5) * 2 * magnitude,
      dy: (state.pointer.y - 0.5) * 2 * magnitude,
    };
  }

  // ---------------------------------------------------------------------
  // Canvas scene: one 2D scene per hero/grooming root, drawn by a concept
  // function keyed off the root's declared concept name.
  // ---------------------------------------------------------------------
  function createScene(canvas, drawFrame, getMotionAllowed) {
    const ctx = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    let dpr = 1;
    let visible = false;
    let running = false;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      width = entry.contentRect.width;
      height = entry.contentRect.height;
      dpr = clamp(window.devicePixelRatio || 1, 1, 2);
      const nextWidth = Math.max(1, Math.round(width * dpr));
      const nextHeight = Math.max(1, Math.round(height * dpr));
      if (canvas.width !== nextWidth) canvas.width = nextWidth;
      if (canvas.height !== nextHeight) canvas.height = nextHeight;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintOnce();
      sync();
    });

    const intersectionObserver = new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting);
      sync();
    }, { threshold: 0.01 });

    const tick = (timestamp) => {
      if (!width || !height) return;
      ctx.clearRect(0, 0, width, height);
      drawFrame(ctx, width, height, timestamp);
    };

    const paintOnce = () => {
      if (!width || !height) return;
      ctx.clearRect(0, 0, width, height);
      drawFrame(ctx, width, height, 0);
    };

    const isInViewport = () => {
      const bounds = canvas.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < window.innerHeight;
    };

    const sync = () => {
      const shouldRun = (visible || isInViewport()) && !document.hidden && getMotionAllowed();
      if (shouldRun && !running) {
        running = true;
        scheduler.add(tick);
      } else if (!shouldRun && running) {
        running = false;
        scheduler.remove(tick);
        paintOnce();
      } else if (!shouldRun) {
        paintOnce();
      }
    };

    const handleVisibility = () => sync();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('scroll', sync, { passive: true });
    resizeObserver.observe(canvas);
    intersectionObserver.observe(canvas);

    return {
      requestRedraw: paintOnce,
      refreshMotion: sync,
      destroy() {
        running = false;
        scheduler.remove(tick);
        resizeObserver.disconnect();
        intersectionObserver.disconnect();
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('scroll', sync);
      },
    };
  }

  // ---------------------------------------------------------------------
  // Shared drawing primitives. Every scene clears to full transparency
  // (see createScene) and only ever paints bounded, translucent shapes —
  // never a full-canvas wash — so the photograph underneath stays legible.
  // ---------------------------------------------------------------------
  function drawLabel(ctx, text, x, y, color = INK, align = 'center', weight = 600) {
    ctx.save();
    ctx.fillStyle = color;
    ctx.font = `${weight} ${clamp(ctx.canvas.clientWidth / 72, 9, 12)}px Arial, sans-serif`;
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x, y);
    ctx.restore();
  }

  function drawPawIcon(ctx, x, y, scale = 1, color = INK, rotation = 0) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.scale(scale, scale);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(0, 7, 11, 9, 0, 0, Math.PI * 2);
    ctx.fill();
    [[-10, -4, 4], [-3.5, -9, 4.2], [4, -9, 4.2], [10, -4, 4]].forEach(([toeX, toeY, radius]) => {
      ctx.beginPath();
      ctx.arc(toeX, toeY, radius, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  function drawCatIcon(ctx, x, y, scale = 1, color = INK, rotation = 0) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.scale(scale, scale);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(0, 2, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(-9, -3);
    ctx.lineTo(-5, -13);
    ctx.lineTo(-1, -3);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(9, -3);
    ctx.lineTo(5, -13);
    ctx.lineTo(1, -3);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    [-1, 0, 1].forEach((row) => {
      ctx.moveTo(-11, 3 + row * 3);
      ctx.lineTo(-20, 1 + row * 4);
      ctx.moveTo(11, 3 + row * 3);
      ctx.lineTo(20, 1 + row * 4);
    });
    ctx.stroke();
    ctx.restore();
  }

  function drawShearsIcon(ctx, x, y, scale = 1, color = INK, rotation = 0) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.scale(scale, scale);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-10, -10);
    ctx.lineTo(10, 10);
    ctx.moveTo(10, -10);
    ctx.lineTo(-10, 10);
    ctx.stroke();
    ctx.fillStyle = 'transparent';
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.arc(-11, -11, 3.4, 0, Math.PI * 2);
    ctx.moveTo(11, -11);
    ctx.arc(11, -11, 3.4, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  function drawDropletIcon(ctx, x, y, scale = 1, color = INK) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, -12);
    ctx.quadraticCurveTo(9, 4, 0, 12);
    ctx.quadraticCurveTo(-9, 4, 0, -12);
    ctx.fill();
    ctx.restore();
  }

  function modeIcon(ctx, x, y, scale, color, mode, rotation = 0) {
    if (mode === 'cat') drawCatIcon(ctx, x, y, scale, color, rotation);
    else if (mode === 'grooming') drawShearsIcon(ctx, x, y, scale, color, rotation);
    else drawPawIcon(ctx, x, y, scale, color, rotation);
  }

  function drawBowl(ctx, x, y, scale = 1, color = INK) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(0, -5, 28, 8, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-28, -5);
    ctx.quadraticCurveTo(-22, 23, 0, 24);
    ctx.quadraticCurveTo(22, 23, 28, -5);
    ctx.stroke();
    ctx.restore();
  }

  function drawDial(ctx, cx, cy, radius, color, fraction, lineWidth = 8) {
    ctx.strokeStyle = 'rgba(36,36,36,0.22)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, -Math.PI / 2, -Math.PI / 2 + clamp(fraction, 0, 1) * Math.PI * 2);
    ctx.stroke();
  }

  const sizeScale = (size) => {
    const index = Math.max(0, SIZE_VALUES.indexOf(size || 'm'));
    return lerp(0.65, 1.35, index / (SIZE_VALUES.length - 1));
  };

  // ---------------------------------------------------------------------
  // Hero concepts. Every function is deterministic (phase-driven, no
  // Math.random) so a reduced-motion single frame is reproducible, and
  // every function branches on state.mode so dog/cat/grooming compose a
  // meaningfully different scene rather than swapping only a label.
  // ---------------------------------------------------------------------
  const HERO_CONCEPTS = {
    'routine-constellation': (ctx, w, h, t, state) => {
      const mode = state.mode || 'dog';
      const accent = modeAccent(mode);
      const drift = pointerDrift(state, 6);
      const cx = w / 2 + drift.dx;
      const cy = h / 2 + drift.dy;
      const scale = sizeScale(state.size);

      if (mode === 'grooming') {
        const stages = GROOM_PATH;
        const serviceStage = { full: 3, bath: 2, quick: 4 }[state.frontService];
        const activeIndex = serviceStage ?? state.highlightIndex;
        const radius = Math.min(w, h) * 0.34;
        stages.forEach((stage, i) => {
          const angle = -Math.PI / 2 + (i / (stages.length - 1)) * Math.PI * 0.9;
          const x = cx + Math.cos(angle) * radius;
          const y = cy + Math.sin(angle) * radius;
          const active = activeIndex === i;
          if (i > 0) {
            const prevAngle = -Math.PI / 2 + ((i - 1) / (stages.length - 1)) * Math.PI * 0.9;
            ctx.strokeStyle = 'rgba(138,115,85,0.28)';
            ctx.lineWidth = 1.4;
            ctx.beginPath();
            ctx.moveTo(cx + Math.cos(prevAngle) * radius, cy + Math.sin(prevAngle) * radius);
            ctx.lineTo(x, y);
            ctx.stroke();
          }
          ctx.fillStyle = active ? accent : INK;
          ctx.beginPath();
          ctx.arc(x, y, active ? 7 : 4, 0, Math.PI * 2);
          ctx.fill();
          drawLabel(ctx, stage, x, y + (active ? 19 : 15), active ? accent : INK);
        });
        modeIcon(ctx, cx, cy, 0.9, accent, 'grooming', t * 0.00006);
        return;
      }

      const needs = mode === 'cat' ? CAT_NEEDS : DOG_NEEDS;
      const radius = Math.min(w, h) * 0.36 * scale;
      ctx.strokeStyle = 'rgba(36,36,36,0.16)';
      ctx.lineWidth = 1;
      needs.forEach((label, i) => {
        const angle = (i / needs.length) * Math.PI * 2 + t * 0.00012;
        const x = cx + Math.cos(angle) * radius;
        const y = cy + Math.sin(angle) * radius;
        if (i > 0) {
          const prevAngle = ((i - 1) / needs.length) * Math.PI * 2 + t * 0.00012;
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(prevAngle) * radius, cy + Math.sin(prevAngle) * radius);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        const active = state.highlightIndex === i;
        ctx.fillStyle = active ? accent : INK;
        ctx.beginPath();
        ctx.arc(x, y, active ? 7 : 4, 0, Math.PI * 2);
        ctx.fill();
        drawLabel(ctx, label, x, y + (active ? 19 : 15), active ? accent : INK);
      });
      modeIcon(ctx, cx, cy, 1.05, accent, mode, t * 0.00008);
    },

    'signal-walk': (ctx, w, h, t, state) => {
      const mode = state.mode || 'dog';
      const accent = modeAccent(mode);
      const drift = pointerDrift(state, 5);

      if (mode === 'cat') {
        // Indoor territory: fixed zone nodes rather than a route.
        const zones = ['WINDOW', 'PERCH', 'LITTER', 'BOWL', 'PLAY'];
        const cols = zones.length;
        zones.forEach((zone, i) => {
          const x = ((i + 0.5) / cols) * w + drift.dx;
          const y = h * (0.42 + (i % 2) * 0.18) + Math.sin(t * 0.0009 + i) * 4;
          const active = state.highlightIndex === i;
          ctx.strokeStyle = 'rgba(79,122,140,0.25)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(x, y, active ? 22 : 15, 0, Math.PI * 2);
          ctx.stroke();
          modeIcon(ctx, x, y, active ? 0.5 : 0.36, active ? accent : INK, 'cat');
          drawLabel(ctx, zone, x, y + (active ? 30 : 24), active ? accent : INK);
        });
        return;
      }

      if (mode === 'grooming') {
        // Appointment route: a vertical journey rather than a horizontal walk.
        const stages = GROOM_PATH;
        const top = h * 0.14;
        const bottom = h * 0.82;
        ctx.strokeStyle = accent;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(w * 0.5 + drift.dx, top);
        ctx.lineTo(w * 0.5 + drift.dx, bottom);
        ctx.stroke();
        stages.forEach((stage, i) => {
          const y = lerp(top, bottom, i / (stages.length - 1));
          const serviceStage = { full: 'TRIM', bath: 'BATHE', quick: 'FINISH' }[state.frontService];
          const active = state.highlightIndex === i || serviceStage === stage;
          ctx.fillStyle = active ? accent : WHITE;
          ctx.strokeStyle = INK;
          ctx.beginPath();
          ctx.arc(w * 0.5 + drift.dx, y, active ? 8 : 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          drawLabel(ctx, stage, w * 0.5 + drift.dx + 64, y, active ? accent : INK, 'left');
        });
        return;
      }

      const steps = 10;
      const baseline = h * 0.66 + drift.dy;
      const points = [];
      drawLabel(ctx, 'HOME', w * 0.08, h * 0.35, INK);
      drawLabel(ctx, 'PAWMART · 1402 YONGE', w * 0.78, h * 0.35, accent);
      ctx.strokeStyle = accent;
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i <= steps; i += 1) {
        const x = (i / steps) * w + drift.dx;
        const wobble = Math.sin(i * 0.9 + t * 0.0016) * h * 0.08 * sizeScale(state.size);
        const y = baseline + wobble;
        points.push({ x, y });
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      points.forEach(({ x, y }, i) => {
        if (i === state.highlightIndex) {
          ctx.save();
          ctx.fillStyle = INK;
          ctx.beginPath();
          ctx.arc(x, y, 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
        if (i > 0 && i < steps && i % 2 === 0) modeIcon(ctx, x, y - 14, .38, i === state.highlightIndex ? accent : INK, 'dog', .35);
      });
      modeIcon(ctx, w * 0.08 + drift.dx, baseline, .72, INK, 'dog');
      modeIcon(ctx, w * 0.92 + drift.dx, baseline, .72, accent, 'dog');
    },

    'feeding-dial': (ctx, w, h, t, state) => {
      const mode = state.mode || 'dog';
      const accent = modeAccent(mode);
      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.min(w, h) * 0.32;
      const scale = sizeScale(state.size);

      if (mode === 'grooming') {
        const fraction = clamp(scale - 0.5, 0, 1);
        drawDial(ctx, cx, cy, radius, accent, fraction);
        const needleAngle = -Math.PI / 2 + fraction * Math.PI * 2;
        ctx.strokeStyle = INK;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(needleAngle) * radius * 0.7, cy + Math.sin(needleAngle) * radius * 0.7);
        ctx.stroke();
        modeIcon(ctx, cx, cy + radius * .42, .8, accent, 'grooming');
        drawLabel(ctx, `${String(state.size || 'm').toUpperCase()} · RATE & TIME`, cx, cy - 4, accent);
        return;
      }

      ctx.strokeStyle = INK;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      const fraction = clamp(scale - 0.5, 0, 1);
      ctx.strokeStyle = accent;
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, -Math.PI / 2, -Math.PI / 2 + fraction * Math.PI * 2);
      ctx.stroke();
      const needleAngle = -Math.PI / 2 + fraction * Math.PI * 2 + Math.sin(t * 0.0008) * 0.02;
      ctx.strokeStyle = INK;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(needleAngle) * radius * 0.7, cy + Math.sin(needleAngle) * radius * 0.7);
      ctx.stroke();
      drawBowl(ctx, cx, cy + radius * .42, .75 * scale, INK);
      drawLabel(ctx, `${String(state.size || 'm').toUpperCase()} ${mode === 'cat' ? 'PORTION' : 'ROUTINE'}`, cx, cy - 4, accent);
    },

    hearth: (ctx, w, h, t, state) => {
      const mode = state.mode || 'dog';
      const accent = modeAccent(mode);
      const scale = sizeScale(state.size);

      if (mode === 'cat') {
        const bands = [['PERCH', 0.18], ['SCRATCH', 0.4], ['HIDE', 0.62], ['LITTER', 0.84]];
        bands.forEach(([label, ratio], i) => {
          const y = h * ratio;
          const active = state.highlightIndex === i;
          ctx.fillStyle = active ? 'rgba(79,122,140,0.24)' : 'rgba(36,36,36,0.10)';
          ctx.fillRect(w * 0.1, y - h * 0.06, w * 0.8, h * 0.1);
          drawLabel(ctx, label, w * 0.5, y - h * 0.01, active ? accent : INK);
        });
        return;
      }

      if (mode === 'grooming') {
        const zones = ['LIGHT', 'WARMTH', 'QUIET', 'CARE'];
        const cx = w / 2;
        const cy = h * 0.58;
        zones.forEach((zone, i) => {
          const radius = (i + 1) * 20 * scale;
          ctx.strokeStyle = `rgba(138,115,85,${0.32 - i * 0.05})`;
          ctx.lineWidth = 6;
          ctx.beginPath();
          ctx.arc(cx, cy, radius, Math.PI * 1.1, Math.PI * 1.9);
          ctx.stroke();
          drawLabel(ctx, zone, cx, cy - radius - 10, i === state.highlightIndex ? accent : INK);
        });
        modeIcon(ctx, cx, cy, .8, accent, 'grooming');
        return;
      }

      const cx = w / 2;
      const cy = h * 0.58;
      const flicker = 1 + Math.sin(t * 0.003) * 0.06;
      const ringScale = scale * flicker;
      for (let ring = 3; ring >= 1; ring -= 1) {
        ctx.fillStyle = ring === 1 ? accent : `rgba(242,138,46,${0.14 * ring})`;
        ctx.beginPath();
        ctx.arc(cx, cy, ring * 22 * ringScale, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = state.highlightIndex != null ? INK : 'rgba(36,36,36,0.6)';
      ctx.beginPath();
      ctx.arc(cx, cy, 8, 0, Math.PI * 2);
      ctx.fill();
      modeIcon(ctx, cx, cy, .92, PAPER, 'dog');
      drawLabel(ctx, 'THE WARM CENTRE OF HOME', cx, cy + 84 * ringScale, INK);
    },

    'filmstrip-shelf': (ctx, w, h, t, state) => {
      const mode = state.mode || 'dog';
      const accent = modeAccent(mode);
      const labels = mode === 'cat' ? CAT_NEEDS : mode === 'grooming'
        ? ['FULL GROOM', 'BATH', 'QUICK CARE', 'NAILS', 'FINISH']
        : DOG_NEEDS;
      const frames = labels.length;
      const gap = 10;
      const visibleFrames = Math.min(5, frames);
      const frameWidth = (w - gap * (visibleFrames + 1)) / visibleFrames;
      const loopWidth = frameWidth + gap;
      const scroll = ((t * .0024) % loopWidth) + (state.railProgress || 0) * gap;
      const frameTop = h * 0.12;
      const frameBottom = h * 0.82;
      const frameHeight = frameBottom - frameTop;
      for (let i = -1; i <= frames; i += 1) {
        const frameIndex = ((i % frames) + frames) % frames;
        const x = gap + i * loopWidth - scroll;
        const active = state.highlightIndex === frameIndex
          || (mode === 'grooming' && (state.frontService || '') === ['full', 'bath', 'quick', 'nails', 'finish'][frameIndex]);
        const lift = active ? -h * 0.025 : 0;
        const y = frameTop + lift;
        const height = frameHeight - lift * 2;

        // Editorial film cells: bounded glass, fine registration marks and
        // generous clear space keep the photograph visible underneath.
        ctx.fillStyle = active ? 'rgba(242,138,46,0.16)' : 'rgba(247,245,239,0.075)';
        ctx.fillRect(x, y, frameWidth, height);
        ctx.strokeStyle = active ? accent : 'rgba(247,245,239,0.72)';
        ctx.lineWidth = active ? 2 : 1;
        ctx.strokeRect(x, y, frameWidth, height);

        for (let sprocket = 0; sprocket < 3; sprocket += 1) {
          const sprocketX = x + frameWidth * ((sprocket + 1) / 4);
          ctx.fillStyle = active ? accent : 'rgba(247,245,239,0.72)';
          ctx.fillRect(sprocketX - 3, y + 7, 6, 3);
          ctx.fillRect(sprocketX - 3, y + height - 10, 6, 3);
        }

        modeIcon(ctx, x + frameWidth / 2, y + height * .46, active ? .72 : .5, active ? accent : PAPER, mode);
        drawLabel(ctx, labels[frameIndex], x + frameWidth / 2, y + height * .72, active ? accent : PAPER);
      }
    },

    'care-bloom': (ctx, w, h, t, state) => {
      const mode = state.mode || 'dog';
      const accent = modeAccent(mode);
      const cx = w / 2;
      const cy = h / 2;
      const scale = sizeScale(state.size);
      const careLabels = mode === 'cat'
        ? ['FEED', 'PLAY', 'REST', 'SCRATCH', 'PURR', 'LOVE']
        : mode === 'grooming'
          ? ['CALM', 'BATHE', 'TRIM', 'FINISH', 'READY', 'CARE']
          : ['FEED', 'WALK', 'PLAY', 'REST', 'GROOM', 'LOVE'];
      const petals = careLabels.length;
      for (let i = 0; i < petals; i += 1) {
        const angle = (i / petals) * Math.PI * 2 + t * 0.0004;
        const active = state.highlightIndex === i;
        const length = (active ? 60 : 44) * scale;
        const x = cx + Math.cos(angle) * length;
        const y = cy + Math.sin(angle) * length;
        ctx.fillStyle = active ? accent : 'rgba(36,36,36,0.30)';
        ctx.beginPath();
        ctx.ellipse(x, y, 16 * scale, 9 * scale, angle, 0, Math.PI * 2);
        ctx.fill();
        drawLabel(ctx, careLabels[i], x, y + 19 * scale, active ? accent : INK);
      }
      ctx.fillStyle = INK;
      ctx.beginPath();
      ctx.arc(cx, cy, 10 * scale, 0, Math.PI * 2);
      ctx.fill();
      modeIcon(ctx, cx, cy, .62 * scale, PAPER, mode);
    },

    'ripple-bowl': (ctx, w, h, t, state) => {
      const mode = state.mode || 'dog';
      const accent = modeAccent(mode);
      const cx = w / 2;
      const cy = h / 2;

      if (mode === 'grooming') {
        const rings = 4;
        for (let i = 0; i < rings; i += 1) {
          const phase = (t * 0.0009 + i / rings) % 1;
          const radius = phase * Math.min(w, h) * 0.4;
          ctx.strokeStyle = `rgba(138,115,85,${1 - phase})`;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(cx, cy, radius, 0, Math.PI * 2);
          ctx.stroke();
        }
        modeIcon(ctx, cx, cy, .78, accent, 'grooming');
        drawLabel(ctx, 'A BATH, CAREFULLY DRAWN', cx, cy - 45, INK);
        return;
      }

      const rings = 4;
      const cadence = mode === 'cat' ? 0.0011 : 0.0006;
      for (let i = 0; i < rings; i += 1) {
        const phase = (t * cadence + i / rings) % 1;
        const radius = phase * Math.min(w, h) * 0.4 * sizeScale(state.size);
        ctx.strokeStyle = mode === 'cat' ? `rgba(79,122,140,${1 - phase})` : `rgba(242,138,46,${1 - phase})`;
        ctx.lineWidth = mode === 'cat' ? 1.4 : 2;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.fillStyle = state.highlightIndex != null ? accent : INK;
      ctx.beginPath();
      ctx.arc(cx, cy, 6, 0, Math.PI * 2);
      ctx.fill();
      drawBowl(ctx, cx, cy + 16, (mode === 'cat' ? .7 : .95) * sizeScale(state.size), INK);
      drawLabel(ctx, mode === 'cat' ? 'FRESH WATER · A LITTLE OFTEN' : 'FRESH WATER · EVERY DAY', cx, cy - 45, INK);
    },

    'orbit-sizes': (ctx, w, h, t, state) => {
      const mode = state.mode || 'dog';
      const accent = modeAccent(mode);

      if (mode === 'cat') {
        // A lifestyle profile for cat supplies: the polygon changes emphasis
        // as a product card is hovered, without pretending cats use dog sizes.
        const cx = w * 0.74;
        const cy = h * 0.43;
        const radius = Math.min(w, h) * 0.25;
        const activeIndex = state.highlightIndex == null ? 2 : state.highlightIndex % CAT_LIFESTYLE.length;
        ctx.strokeStyle = 'rgba(79,122,140,0.24)';
        ctx.lineWidth = 1;
        [0.45, 0.72, 1].forEach((ring) => {
          ctx.beginPath();
          CAT_LIFESTYLE.forEach((label, index) => {
            const angle = -Math.PI / 2 + (index / CAT_LIFESTYLE.length) * Math.PI * 2;
            const x = cx + Math.cos(angle) * radius * ring;
            const y = cy + Math.sin(angle) * radius * ring;
            if (index === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.stroke();
        });
        ctx.fillStyle = 'rgba(79,122,140,0.10)';
        ctx.strokeStyle = accent;
        ctx.lineWidth = 2;
        ctx.beginPath();
        CAT_LIFESTYLE.forEach((label, index) => {
          const angle = -Math.PI / 2 + (index / CAT_LIFESTYLE.length) * Math.PI * 2;
          const pulse = index === activeIndex ? 1 : 0.68 + Math.sin(t * 0.001 + index) * 0.025;
          const x = cx + Math.cos(angle) * radius * pulse;
          const y = cy + Math.sin(angle) * radius * pulse;
          if (index === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        CAT_LIFESTYLE.forEach((label, index) => {
          const angle = -Math.PI / 2 + (index / CAT_LIFESTYLE.length) * Math.PI * 2;
          const x = cx + Math.cos(angle) * radius * 1.18;
          const y = cy + Math.sin(angle) * radius * 1.18;
          drawLabel(ctx, label, x, y, index === activeIndex ? accent : PAPER);
        });
        modeIcon(ctx, cx, cy, .68, PAPER, 'cat');
        return;
      }

      if (mode === 'grooming') {
        // A service-duration instrument, driven only by real service cards.
        const tracks = [
          ['FULL SERVICE', '2–3 H', 'full'],
          ['BATH + BRUSH', '1–2 H', 'bath'],
          ['QUICK CARE', 'FROM 10 MIN', 'quick'],
        ];
        const x1 = w * 0.56;
        const x2 = w * 0.91;
        tracks.forEach(([label, duration, key], index) => {
          const y = h * (0.30 + index * 0.18);
          const active = state.frontService === key || (!state.frontService && index === 0);
          ctx.strokeStyle = active ? accent : 'rgba(247,245,239,0.46)';
          ctx.lineWidth = active ? 3 : 1;
          ctx.beginPath();
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
          ctx.stroke();
          ctx.fillStyle = active ? accent : PAPER;
          ctx.beginPath();
          ctx.arc(x1 + (x2 - x1) * (active ? 0.74 : 0.24), y, active ? 7 : 4, 0, Math.PI * 2);
          ctx.fill();
          drawLabel(ctx, label, x1, y - 15, active ? accent : PAPER, 'left');
          drawLabel(ctx, duration, x2, y - 15, active ? accent : PAPER, 'right');
        });
        modeIcon(ctx, w * 0.48, h * 0.48, .78, accent, 'grooming');
        return;
      }

      // Dog-supply fit studio: a moving tape and useful measurement
      // references replace decorative orbits. The chosen size changes scale.
      const scale = sizeScale(state.size);
      const scan = reducedQuery.matches ? 0.5 : (Math.sin(t * 0.0012) + 1) / 2;
      const left = w * 0.55;
      const right = w * (0.78 + (scale - 1) * 0.05);
      const top = h * 0.27;
      const bottom = h * (0.72 + (scale - 1) * 0.04);
      const mid = lerp(top, bottom, scan);

      ctx.strokeStyle = 'rgba(247,245,239,0.68)';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 6]);
      ctx.strokeRect(left, top, right - left, bottom - top);
      ctx.setLineDash([]);

      ctx.strokeStyle = accent;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(left - 12, mid);
      ctx.lineTo(right + 12, mid);
      ctx.stroke();

      const guides = [
        ['NECK', left + (right - left) * 0.72, top + (bottom - top) * 0.16],
        ['CHEST', left + (right - left) * 0.42, top + (bottom - top) * 0.52],
        ['BACK', left + (right - left) * 0.64, top + (bottom - top) * 0.80],
      ];
      guides.forEach(([label, x, y], index) => {
        const active = state.highlightIndex != null && state.highlightIndex % guides.length === index;
        ctx.strokeStyle = active ? accent : 'rgba(247,245,239,0.74)';
        ctx.fillStyle = active ? accent : PAPER;
        ctx.beginPath();
        ctx.arc(x, y, active ? 7 : 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(right + 22, y);
        ctx.stroke();
        drawLabel(ctx, label, right + 28, y, active ? accent : PAPER, 'left');
      });
      drawLabel(ctx, `FIT STUDIO · ${String(state.size || 'm').toUpperCase()}`, left, top - 18, accent, 'left');
      drawLabel(ctx, 'MEASURE · TRY · WALK', left, bottom + 18, PAPER, 'left');
    },

    sketchbook: (ctx, w, h, t, state) => {
      const mode = state.mode || 'dog';
      const accent = modeAccent(mode);
      ctx.strokeStyle = 'rgba(36,36,36,0.14)';
      ctx.lineWidth = 1;
      const lines = 8;
      for (let i = 1; i < lines; i += 1) {
        const y = (i / lines) * h;
        ctx.beginPath();
        ctx.moveTo(w * 0.08, y);
        ctx.lineTo(w * 0.92, y);
        ctx.stroke();
      }
      const open = state.noteOpen ? 1 : 0.35;
      ctx.strokeStyle = accent;
      ctx.lineWidth = 3;
      const wobble = Math.sin(t * 0.001) * 4;
      ctx.beginPath();
      ctx.moveTo(w * 0.15, h * 0.3 + wobble);
      ctx.quadraticCurveTo(w * 0.5, h * (0.15 * open + 0.2), w * 0.85, h * 0.3 - wobble);
      ctx.stroke();
      const caption = mode === 'cat'
        ? 'A TERRITORY, MAPPED WITH CARE'
        : mode === 'grooming'
          ? 'A VISIT, PLANNED WITH CARE'
          : 'A ROUTINE, DRAWN TOGETHER';
      modeIcon(ctx, w * .24, h * .55, mode === 'grooming' ? 1 : 1.2, 'rgba(36,36,36,0.5)', mode, -.18);
      drawLabel(ctx, caption, w * .58, h * .55, INK);
    },

    'proximity-shelf': (ctx, w, h, t, state) => {
      const mode = state.mode || 'dog';
      const accent = modeAccent(mode);
      const labels = mode === 'cat' ? CAT_NEEDS.slice(0, 5) : mode === 'grooming'
        ? ['FULL', 'BATH', 'QUICK', 'NAILS', 'FINISH']
        : DOG_NEEDS.slice(0, 5);
      const columns = labels.length;
      const colWidth = w / columns;
      const drift = pointerDrift(state, 8);
      for (let i = 0; i < columns; i += 1) {
        const active = state.highlightIndex === i
          || (mode === 'grooming' && (state.frontService || '') === labels[i].toLowerCase());
        // Cylindrical gallery: columns near the pointer curve forward and grow.
        const centreDistance = Math.abs((i + 0.5) / columns - state.pointer.x);
        const curve = reducedQuery.matches ? 0 : (1 - clamp(centreDistance * 2.2, 0, 1)) * 10;
        const targetHeight = active ? h * 0.7 : h * 0.32;
        const barHeight = targetHeight + Math.sin(t * 0.0015 + i) * (active ? 4 : 1) + curve;
        ctx.fillStyle = active ? accent : 'rgba(36,36,36,0.24)';
        const x = i * colWidth + colWidth * 0.18 + drift.dx * (i / columns - 0.5);
        ctx.fillRect(x, h - barHeight, colWidth * 0.64, barHeight);
        modeIcon(ctx, x + colWidth * .32, h - barHeight * .58, active ? .72 : .48, active ? INK : PAPER, mode);
        drawLabel(ctx, labels[i], x + colWidth * .32, h - 18, active ? INK : PAPER);
      }
    },
  };

  // ---------------------------------------------------------------------
  // Grooming (lower section) concepts: service interactions only, no
  // product illustration.
  // ---------------------------------------------------------------------
  const GROOM_CONCEPTS = {
    'weight-dial': (ctx, w, h, t, state) => {
      const accent = GROOM_ACCENT;
      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.min(w, h) * 0.34;
      const fraction = clamp(sizeScale(state.weight) - 0.5, 0, 1);
      drawDial(ctx, cx, cy, radius, state.service === 'bath' ? INK : accent, fraction);
      SIZE_VALUES.forEach((size, index) => {
        const angle = -Math.PI / 2 + (index / (SIZE_VALUES.length - 1)) * Math.PI * 2;
        const x = cx + Math.cos(angle) * radius * .78;
        const y = cy + Math.sin(angle) * radius * .78;
        drawLabel(ctx, size.toUpperCase(), x, y, size === state.weight ? accent : INK);
      });
      modeIcon(ctx, cx, cy, 1.05, state.service === 'bath' ? INK : accent, 'grooming', t * 0.00005);
      drawLabel(ctx, 'RATE & TIME BY SIZE', cx, cy + radius * .54, INK);
    },

    'appointment-timeline': (ctx, w, h, t, state) => {
      const accent = GROOM_ACCENT;
      const y = h / 2;
      const stages = ['BOOK', ...GROOM_PATH.slice(1), 'READY'];
      ctx.strokeStyle = INK;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(w * 0.08, y);
      ctx.lineTo(w * 0.92, y);
      ctx.stroke();
      const slots = stages.length;
      const pointerSlot = reducedQuery.matches || !state.pointer.active
        ? null
        : Math.round(clamp(state.pointer.x, 0, 1) * (slots - 1));
      for (let i = 0; i < slots; i += 1) {
        const x = lerp(w * 0.08, w * 0.92, i / (slots - 1));
        const active = state.highlightIndex === i || pointerSlot === i;
        ctx.fillStyle = active ? accent : WHITE;
        ctx.strokeStyle = INK;
        ctx.beginPath();
        ctx.arc(x, y, active ? 9 : 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        drawLabel(ctx, stages[i], x, y + 24, active ? accent : INK);
      }
      modeIcon(ctx, lerp(w * .08, w * .92, ((t * .00006) % 1)), y - 24, .52, accent, 'grooming');
    },

    'quick-service-constellation': (ctx, w, h, t, state) => {
      const accent = GROOM_ACCENT;
      // Mark the photographed dog itself instead of drawing a second generic
      // silhouette. Quick-service chips highlight the matching point of care.
      const markers = [
        [0.74, 0.34], // face
        [0.66, 0.31], // ears
        [0.77, 0.40], // teeth
        [0.65, 0.83], // nails
        [0.78, 0.62], // tidy
      ];
      const quickToMarker = [3, 3, 3, 0, 1, 1, 2, 4, 4];
      const activeMarker = state.highlightIndex == null ? 3 : quickToMarker[state.highlightIndex] ?? 4;
      const labelX = w * 0.10;
      const elbowX = w * 0.44;

      QUICK_SERVICE_MARKERS.forEach((label, index) => {
        const [xRatio, yRatio] = markers[index];
        const x = w * xRatio;
        const y = h * yRatio;
        const labelY = h * (0.25 + index * 0.12);
        const active = activeMarker === index;
        const pulse = active && !reducedQuery.matches ? 2 + Math.sin(t * 0.003) * 1.5 : 0;

        ctx.strokeStyle = active ? accent : 'rgba(247,245,239,0.58)';
        ctx.lineWidth = active ? 2 : 1;
        ctx.beginPath();
        ctx.moveTo(labelX + 58, labelY);
        ctx.lineTo(elbowX, labelY);
        ctx.lineTo(x, y);
        ctx.stroke();

        ctx.fillStyle = active ? accent : PAPER;
        ctx.beginPath();
        ctx.arc(x, y, (active ? 6 : 3.5) + pulse, 0, Math.PI * 2);
        ctx.fill();
        drawLabel(ctx, label, labelX, labelY, active ? accent : PAPER, 'left');
      });
      drawLabel(ctx, 'QUICK CARE · PICK A SERVICE', labelX, h * 0.16, accent, 'left');
    },

    'ripple-bath': (ctx, w, h, t, state) => {
      const accent = GROOM_ACCENT;
      const cx = w / 2;
      const cy = h / 2;
      const rings = 4;
      const speed = state.service === 'bath' ? 0.0009 : 0.0004;
      for (let i = 0; i < rings; i += 1) {
        const phase = (t * speed + i / rings) % 1;
        const radius = phase * Math.min(w, h) * 0.4;
        ctx.strokeStyle = `rgba(138,115,85,${1 - phase})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.stroke();
      }
      // Fogged bath-glass pane: a bounded translucent gradient, not a full wash.
      const paneWidth = w * 0.42;
      const paneHeight = h * 0.6;
      const paneX = w * 0.06;
      const paneY = h * 0.2;
      const gradient = ctx.createLinearGradient(paneX, paneY, paneX, paneY + paneHeight);
      gradient.addColorStop(0, 'rgba(247,245,239,0.22)');
      gradient.addColorStop(1, 'rgba(247,245,239,0.05)');
      ctx.fillStyle = gradient;
      ctx.fillRect(paneX, paneY, paneWidth, paneHeight);
      for (let drop = 0; drop < 5; drop += 1) {
        const phase = (t * 0.0003 + drop * 0.21) % 1;
        const x = paneX + paneWidth * ((drop + 1) / 6);
        const y = paneY + phase * paneHeight;
        drawDropletIcon(ctx, x, y, 0.45, 'rgba(138,115,85,0.35)');
      }
      drawBowl(ctx, cx, cy + 18, 1.05, INK);
      modeIcon(ctx, cx, cy - 8, .8, accent, 'grooming');
    },

    'coat-flow': (ctx, w, h, t, state) => {
      const accent = GROOM_ACCENT;
      const strands = 6;
      for (let i = 0; i < strands; i += 1) {
        const active = state.highlightIndex === i;
        ctx.strokeStyle = active ? accent : 'rgba(36,36,36,0.22)';
        ctx.lineWidth = active ? 3 : 1.5;
        ctx.beginPath();
        for (let x = 0; x <= w; x += w / 24) {
          const y = h / 2 + Math.sin(x * 0.02 + i + t * 0.0012) * (h * 0.08) * sizeScale(state.weight);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      drawShearsIcon(ctx, w * .5, h * .5, .9, accent, Math.PI / 4);
      // Finish gloss sweep: a bounded diagonal band, clipped so it never washes the frame.
      ctx.save();
      ctx.beginPath();
      ctx.rect(w * 0.1, h * 0.15, w * 0.8, h * 0.7);
      ctx.clip();
      const sweep = ((t * 0.0004) % 1.6) - 0.3;
      const bandX = w * sweep;
      const bandGradient = ctx.createLinearGradient(bandX, 0, bandX + w * 0.16, h);
      bandGradient.addColorStop(0, 'rgba(255,255,255,0)');
      bandGradient.addColorStop(0.5, 'rgba(255,255,255,0.22)');
      bandGradient.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = bandGradient;
      ctx.fillRect(w * 0.1, h * 0.15, w * 0.8, h * 0.7);
      ctx.restore();
      drawLabel(ctx, 'BRUSH · SHAPE · FINISH', w * .5, h * .82, INK);
    },
  };

  // ---------------------------------------------------------------------
  // Front (hero) root instance.
  // ---------------------------------------------------------------------
  function initFront(root) {
    if (!root || instances.has(root)) return;

    const dock = root.querySelector('[data-pmx-h6-dock]');
    const buttons = Array.from(root.querySelectorAll('[data-pmx-h6-mode]'));
    const panels = Array.from(root.querySelectorAll('[data-pmx-h6-panel]'));
    if (!dock || !buttons.length || !panels.length) return;

    const controller = new AbortController();
    const { signal } = controller;
    const states = buttons.map((button) => ({
      button,
      inner: button.querySelector('.pmx-h6-front__dock-item-inner'),
      value: 0,
      velocity: 0,
      target: 0,
    }));

    const spring = 0.16;
    const damping = 0.68;
    let autoModeTimer = 0;
    let autoModeStopped = false;
    let autoModeVisible = false;
    let autoModeObserver = null;

    const renderState = (state) => {
      if (!state.inner) return;
      const value = clamp(state.value, 0, 1);
      state.inner.style.transform = `translateY(${-2.5 * value}px) scale(${1 + 0.075 * value})`;
    };

    const dockTick = () => {
      let moving = false;
      for (const state of states) {
        state.velocity += (state.target - state.value) * spring;
        state.velocity *= damping;
        state.value += state.velocity;
        if (Math.abs(state.target - state.value) < 0.002 && Math.abs(state.velocity) < 0.002) {
          state.value = state.target;
          state.velocity = 0;
        } else {
          moving = true;
        }
        renderState(state);
      }
      if (!moving) scheduler.remove(dockTick);
    };

    const startDockSpring = () => {
      if (reducedQuery.matches || !precisionQuery.matches) {
        scheduler.remove(dockTick);
        for (const state of states) {
          state.value = 0;
          state.velocity = 0;
          state.target = 0;
          renderState(state);
        }
        return;
      }
      scheduler.add(dockTick);
    };

    const resetDock = () => {
      for (const state of states) state.target = 0;
      startDockSpring();
    };

    const setDockTargets = (clientX) => {
      if (reducedQuery.matches || !precisionQuery.matches) return;
      const influenceRadius = 116;
      for (const state of states) {
        const bounds = state.button.getBoundingClientRect();
        const centre = bounds.left + bounds.width / 2;
        const proximity = clamp(1 - Math.abs(clientX - centre) / influenceRadius, 0, 1);
        state.target = proximity * proximity * (3 - 2 * proximity);
      }
      startDockSpring();
    };

    // Scene state shared with the concept draw functions.
    const sceneState = {
      mode: buttons[0]?.dataset.pmxH6Mode || 'dog',
      size: readStoredSize() || 'm',
      highlightIndex: null,
      frontService: null,
      railProgress: 0,
      noteOpen: false,
      pointer: { x: 0.5, y: 0.5, active: false },
    };

    const hasHeroConcept = root.matches('[data-pmx-h6-front][data-pmx-h6-hero-concept]');
    const concept = root.dataset.pmxH6HeroConcept;
    const drawConcept = hasHeroConcept ? HERO_CONCEPTS[concept] : undefined;
    const canvas = root.querySelector('[data-pmx-h6-hero-canvas]');
    let scene = null;

    const attachCanvasToPanel = (panel) => {
      if (!canvas) return;
      const media = panel?.querySelector('.pmx-h6-front__media, .pmx-h6-grooming__media');
      if (media && canvas.parentElement !== media) {
        media.appendChild(canvas);
      }
      canvas.hidden = !media;
    };

    if (canvas && drawConcept) {
      scene = createScene(
        canvas,
        (ctx, w, h, t) => drawConcept(ctx, w, h, t, sceneState),
        () => !reducedQuery.matches,
      );
    }

    const setActive = (key, source = 'manual') => {
      const previousKey = sceneState.mode;
      const activeButton = buttons.find((button) => button.dataset.pmxH6Mode === key) || buttons[0];
      const activeKey = activeButton.dataset.pmxH6Mode;
      root.dataset.mode = activeKey;
      sceneState.mode = activeKey;
      sceneState.highlightIndex = null;
      sceneState.frontService = null;

      for (const button of buttons) {
        button.setAttribute('aria-pressed', String(button === activeButton));
      }
      for (const panel of panels) {
        const active = panel.dataset.pmxH6Panel === activeKey;
        panel.hidden = !active;
        panel.dataset.active = String(active);
        if (active) attachCanvasToPanel(panel);
      }
      scene?.refreshMotion();
      scene?.requestRedraw();

      if (previousKey !== activeKey && root.classList.contains('is-enhanced')) {
        root.dispatchEvent(new CustomEvent('pmx:h6-mode-transition', {
          detail: { from: previousKey, to: activeKey, automatic: source === 'auto' },
        }));
      }
    };

    function clearAutoMode() {
      if (!autoModeTimer) return;
      window.clearTimeout(autoModeTimer);
      autoModeTimer = 0;
    }

    function scheduleAutoMode() {
      clearAutoMode();
      if (autoModeStopped || !autoModeVisible || document.hidden || reducedQuery.matches) return;
      autoModeTimer = window.setTimeout(advanceAutoMode, AUTO_MODE_DELAY);
    }

    function advanceAutoMode() {
      autoModeTimer = 0;
      if (autoModeStopped || !autoModeVisible || document.hidden || reducedQuery.matches) return;
      const activeIndex = Math.max(0, buttons.findIndex((button) => button.dataset.pmxH6Mode === sceneState.mode));
      const nextButton = buttons[(activeIndex + 1) % buttons.length];
      setActive(nextButton.dataset.pmxH6Mode, 'auto');
      scheduleAutoMode();
    }

    function stopAutoMode() {
      autoModeStopped = true;
      clearAutoMode();
      root.dataset.pmxH6AutoRotate = 'stopped';
    }

    dock.addEventListener('pointermove', (event) => setDockTargets(event.clientX), { signal, passive: true });
    dock.addEventListener('pointerleave', resetDock, { signal });
    dock.addEventListener('focusin', (event) => {
      const button = event.target.closest('[data-pmx-h6-mode]');
      const index = buttons.indexOf(button);
      if (index < 0 || reducedQuery.matches || !precisionQuery.matches) return;
      states.forEach((state, stateIndex) => {
        state.target = stateIndex === index ? 1 : Math.abs(stateIndex - index) === 1 ? 0.22 : 0;
      });
      startDockSpring();
    }, { signal });
    dock.addEventListener('focusout', () => window.setTimeout(() => {
      if (!dock.contains(document.activeElement)) resetDock();
    }, 0), { signal });
    dock.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const current = buttons.indexOf(document.activeElement);
      if (current < 0) return;
      event.preventDefault();
      let next = current;
      if (event.key === 'ArrowLeft') next = (current - 1 + buttons.length) % buttons.length;
      if (event.key === 'ArrowRight') next = (current + 1) % buttons.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = buttons.length - 1;
      buttons[next].focus();
    }, { signal });

    for (const button of buttons) {
      button.addEventListener('click', (event) => {
        if (event.isTrusted) stopAutoMode();
        setActive(button.dataset.pmxH6Mode, 'manual');
        resetDock();
      }, { signal });
    }

    root.addEventListener('focusin', (event) => {
      if (event.isTrusted) stopAutoMode();
    }, { signal });

    const handleAutoVisibility = () => scheduleAutoMode();
    document.addEventListener('visibilitychange', handleAutoVisibility);
    autoModeObserver = new IntersectionObserver((entries) => {
      autoModeVisible = entries[0]?.isIntersecting === true;
      scheduleAutoMode();
    }, { threshold: 0.22 });
    autoModeObserver.observe(root);

    const handleCapabilityChange = () => {
      resetDock();
      scene?.refreshMotion();
      scheduleAutoMode();
    };
    reducedQuery.addEventListener('change', handleCapabilityChange);
    precisionQuery.addEventListener('change', handleCapabilityChange);

    // Product rails: hover/focus highlight and prev/next paging, one per panel.
    // Delegate variant, quantity, cart and modal focus to the existing Eurus store.
    // A normal product link remains usable before Alpine loads and without JS.
    wireQuickview(root, signal);
    root.querySelectorAll('[data-pmx-h6-rail]').forEach((rail) => wireRail(rail, signal));
    wireHighlight(root, '[data-pmx-h6-product]', signal, (element) => {
      sceneState.highlightIndex = element ? Number(element.dataset.pmxH6Product) : null;
      scene?.requestRedraw();
    });

    // Grooming service controls (full/bath/quick), wired exactly like product hover/focus.
    wireHighlight(root, '[data-pmx-h6-front-service]', signal, (element) => {
      sceneState.frontService = element?.dataset.pmxH6FrontService || null;
      scene?.requestRedraw();
    });

    // Pointer position/activity from the active media, driving depth and drift only.
    wirePointer(root, '.pmx-h6-front__media', signal, sceneState, () => scene?.requestRedraw());

    // Optional size buttons within this root feed the shared session state.
    root.querySelectorAll('[data-pmx-h6-size]').forEach((button) => {
      button.addEventListener('click', () => applyGlobalSize(button.dataset.pmxH6Size), { signal });
    });

    const noteToggle = root.querySelector('[data-pmx-h6-note-toggle]');
    noteToggle?.addEventListener('click', () => {
      sceneState.noteOpen = !sceneState.noteOpen;
      root.dataset.noteOpen = String(sceneState.noteOpen);
      noteToggle.setAttribute('aria-expanded', String(sceneState.noteOpen));
      scene?.requestRedraw();
    }, { signal });

    root.classList.add('is-enhanced');
    root.dataset.size = sceneState.size;
    root.querySelectorAll('[data-pmx-h6-size]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.pmxH6Size === sceneState.size));
    });
    setActive(buttons.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset.pmxH6Mode, 'initial');
    root.dataset.pmxH6AutoRotate = 'active';

    instances.set(root, {
      kind: 'front',
      applySize(size) {
        sceneState.size = size;
        root.dataset.size = size;
        root.querySelectorAll('[data-pmx-h6-size]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.pmxH6Size === size));
        });
        scene?.requestRedraw();
      },
      destroy() {
        clearAutoMode();
        autoModeObserver?.disconnect();
        document.removeEventListener('visibilitychange', handleAutoVisibility);
        scheduler.remove(dockTick);
        controller.abort();
        reducedQuery.removeEventListener('change', handleCapabilityChange);
        precisionQuery.removeEventListener('change', handleCapabilityChange);
        scene?.destroy();
        root.classList.remove('is-enhanced');
        for (const panel of panels) panel.hidden = false;
        for (const state of states) {
          if (state.inner) state.inner.style.transform = '';
        }
        if (canvas) canvas.hidden = true;
      },
    });
  }

  // ---------------------------------------------------------------------
  // Grooming root instance.
  // ---------------------------------------------------------------------
  function initGrooming(root) {
    if (!root || instances.has(root)) return;

    const hasGroomConcept = root.matches('[data-pmx-h6-grooming][data-pmx-h6-groom-concept]');
    const canvas = root.querySelector('[data-pmx-h6-groom-canvas]');
    const concept = root.dataset.pmxH6GroomConcept;
    const drawConcept = hasGroomConcept ? GROOM_CONCEPTS[concept] : undefined;
    const controller = new AbortController();
    const { signal } = controller;

    const sceneState = {
      mode: 'grooming',
      size: readStoredSize() || 'm',
      weight: readStoredSize() || 'm',
      service: null,
      highlightIndex: null,
      pointer: { x: 0.5, y: 0.5, active: false },
    };

    const updateSizeReadout = (size) => {
      const button = root.querySelector(`[data-pmx-h6-groom-size="${size}"]`);
      const readout = root.querySelector('[data-pmx-h6-groom-size-readout]');
      if (!button || !readout) return;
      const label = button.querySelector('span')?.textContent?.trim() || size.toUpperCase();
      const range = button.dataset.pmxH6GroomRange || '';
      readout.textContent = `${label}${range ? ` · ${range}` : ''} selected`;
      root.dataset.selectedRange = range;
    };

    const revealWeightColumn = (size) => {
      const wrap = root.querySelector('.pmx-h6-grooming__table-wrap');
      const column = root.querySelector(`thead [data-pmx-h6-weight="${size}"]`);
      if (!wrap || !column || wrap.scrollWidth <= wrap.clientWidth) return;
      const left = column.offsetLeft - (wrap.clientWidth - column.offsetWidth) / 2;
      wrap.scrollTo({
        left: Math.max(0, left),
        behavior: reducedQuery.matches ? 'auto' : 'smooth',
      });
    };

    let scene = null;
    if (canvas && drawConcept) {
      const media = root.querySelector('.pmx-h6-grooming__media');
      if (media && canvas.parentElement !== media) media.appendChild(canvas);
      scene = createScene(
        canvas,
        (ctx, w, h, t) => drawConcept(ctx, w, h, t, sceneState),
        () => !reducedQuery.matches,
      );
    }

    const rows = Array.from(root.querySelectorAll('[data-pmx-h6-service-row]'));
    wireHighlight(root, '[data-pmx-h6-service-row]', signal, (element) => {
      sceneState.service = element?.dataset.pmxH6ServiceRow || null;
      sceneState.highlightIndex = element ? rows.indexOf(element) : null;
      scene?.requestRedraw();
    });

    const quickItems = Array.from(root.querySelectorAll('[data-pmx-h6-quick]'));
    wireHighlight(root, '[data-pmx-h6-quick]', signal, (element) => {
      sceneState.highlightIndex = element ? quickItems.indexOf(element) : null;
      scene?.requestRedraw();
    });

    wireHighlight(root, '[data-pmx-h6-weight]', signal, (element) => {
      sceneState.weight = element?.dataset.pmxH6Weight || sceneState.size || 'm';
      scene?.requestRedraw();
    });

    wirePointer(root, '.pmx-h6-grooming__media', signal, sceneState, () => scene?.requestRedraw());

    root.querySelectorAll('[data-pmx-h6-groom-size]').forEach((button) => {
      button.addEventListener('click', () => {
        const size = button.dataset.pmxH6GroomSize;
        applyGlobalSize(size);
        revealWeightColumn(size);
      }, { signal });
    });

    root.dataset.size = sceneState.size;
    root.querySelectorAll('[data-pmx-h6-groom-size]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.pmxH6GroomSize === sceneState.size));
    });
    updateSizeReadout(sceneState.size);

    instances.set(root, {
      kind: 'grooming',
      applySize(size) {
        sceneState.size = size;
        sceneState.weight = size;
        root.dataset.size = size;
        root.querySelectorAll('[data-pmx-h6-groom-size]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.pmxH6GroomSize === size));
        });
        updateSizeReadout(size);
        scene?.requestRedraw();
      },
      destroy() {
        controller.abort();
        scene?.destroy();
      },
    });
  }

  // ---------------------------------------------------------------------
  // Size selection: sessionStorage-backed, applied to every live root.
  // ---------------------------------------------------------------------
  function applyGlobalSize(size) {
    if (!SIZE_VALUES.includes(size)) return;
    writeStoredSize(size);
    for (const instance of instances.values()) instance.applySize(size);
  }

  function initWithin(scope = document) {
    if (scope.matches?.('[data-pmx-h6-front]')) initFront(scope);
    scope.querySelectorAll?.('[data-pmx-h6-front]').forEach(initFront);
    if (scope.matches?.('[data-pmx-h6-grooming]')) initGrooming(scope);
    scope.querySelectorAll?.('[data-pmx-h6-grooming]').forEach(initGrooming);
  }

  function destroyWithin(scope) {
    const roots = [];
    if (scope.matches?.('[data-pmx-h6-front], [data-pmx-h6-grooming]')) roots.push(scope);
    scope.querySelectorAll?.('[data-pmx-h6-front], [data-pmx-h6-grooming]').forEach((root) => roots.push(root));
    for (const root of roots) {
      instances.get(root)?.destroy();
      instances.delete(root);
    }
  }

  window.PMXHome6 = { initWithin, destroyWithin };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
